import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers/DateField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { CellWrapper, useCellContext } from "@silevis/reactgrid";
import dayjs, { Dayjs } from "dayjs";
import moment, { Moment } from "moment";
import { FC, useEffect, useRef, useState } from "react";
import { shortcutType } from "./reactGridHelper";
import styles from './CustomDateCell.module.scss';
import { textAlign } from "@mui/system";

interface DateCellProps {
    value: string;
    onValueChanged: (data: string) => void;
    format: string;
    onAddRow?: (rowIndex: number) => void;
    onDeleteRow?: (rowIndex: number) => void;
    allowShortcuts?: shortcutType[];
}

const dateFieldFontStyle = {
    "md": {
        fontFamily: "Work Sans",
        fontSize: "0.6875rem",
        fontStyle: "normal",
        fontWeight: "500",
        lineHeight: "0.875rem",
        letterSpacing: "0.00688rem",
    },
    "md2": {
        fontFamily: "Work Sans",
        fontSize: "0.8125rem",
        fontStyle: "normal",
        fontWeight: "500",
        lineHeight: "1.125rem", /* 128.571% */
        letterSpacing: "0.00875rem",
    }
}


export const DateCell: FC<DateCellProps> = ({
    value,
    onValueChanged,
    format = "DD/MM/YYYY",
    onAddRow,
    onDeleteRow,
    allowShortcuts = [] }) => {
    const ctx = useCellContext();
    const targetInputRef = useRef<HTMLInputElement>(null);
    const [isEditMode, setEditMode] = useState(false);
    const dateFieldRef = useRef<HTMLInputElement | null>(null);
    const [currentValue, setCurrentValue] = useState<Dayjs>(value ? dayjs(value, "DD/MM/YYYY") : null);
    const escapePressedRef = useRef(false);


    let formattedDate: string | undefined;

    useEffect(() => {
        setCurrentValue(value ? dayjs(value, "DD/MM/YYYY") : null);
    }, [value]);

    if (!value) {
        formattedDate = "";
    } else {
        formattedDate = dayjs(value).format("DD/MM/YYYY");
    }

    useEffect(() => {
        if (!ctx.isFocused) return; // Ensure shortcut only works when cell is focused

        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "+" || e.key === "=")) {
                e.preventDefault();
                if (allowShortcuts.includes(shortcutType.addRow)) {
                    console.log('ctx.realRowIndex', ctx.realRowIndex)
                    onAddRow?.(ctx.realRowIndex - 1);
                }
            } else if ((e.ctrlKey || e.metaKey) && e.key === "-") {
                e.preventDefault();
                if (allowShortcuts.includes(shortcutType.deleteRow)) {
                    onDeleteRow?.(ctx.realRowIndex - 1);
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [ctx.isFocused, onAddRow, onDeleteRow, allowShortcuts]);


    const exitEditMode = (value: Dayjs) => {
        setEditMode(false);
        const isValid = value.isValid();
        if (isValid) {
            onValueChanged?.(value.format("DD/MM/YYYY"));
        } else {
            onValueChanged(""); // Handle invalid date case
        }
    }

    return (
        <CellWrapper
            onStringValueRequested={() => dayjs(value, "DD/MM/YYYY").format("DD/MM/YYYY")}
            onStringValueReceived={(v) => {
                console.log('string value received', v)
                return onValueChanged?.(dayjs(v, "DD/MM/YYYY").format("DD/MM/YYYY"));
            }}
            onDoubleClick={() => {
                ctx.isFocused && setEditMode(true);
            }}
            onKeyDown={(e) => {
                if (!isEditMode && e.key === "Enter") {
                    e.stopPropagation();
                    setEditMode(true);
                }
            }}
        >
            {isEditMode ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateField
                        formatDensity='spacious'
                        variant="standard"
                        format={format}
                        ref={dateFieldRef}
                        InputProps={{
                            disableUnderline: true,
                        }}
                        autoFocus
                        onBlur={(e) => {
                            console.log('on blur triggered from date field', e.target.value)
                            if (!escapePressedRef.current) {
                                exitEditMode(dayjs(currentValue));
                            }
                            if (escapePressedRef.current) {
                                escapePressedRef.current = false;
                            }
                        }}
                        inputRef={targetInputRef}
                        onPointerDown={(e) => e.stopPropagation()}
                        classes={{
                            root: styles.dateFieldRoot,
                        }}
                        inputProps={{
                            onKeyDown: (e) => {
                                console.log('onKeyDown', e.key)
                                const controlKeys = ["Escape", "Enter", "Tab"];
                                if (!controlKeys.includes(e.key)) {
                                    e.stopPropagation();
                                }
                                const value = dayjs(e.currentTarget.value);
                                // const formattedValue = value?.isValid() ? value : "";
                                if (e.key === "Escape") {
                                    exitEditMode(value)
                                } else if (e.key === "Enter") {
                                    exitEditMode(value);
                                } else if (e.key === "Tab") {
                                    // onValueChanged?.(formattedValue);
                                    exitEditMode(value);
                                }
                            },
                            onFocus: (e) => {
                                console.log('coming in focus inner date field')
                            },
                            style: {
                                input: {
                                    textAlign: "center",
                                    '&::placeholder': {
                                        color: "#aaaaaa",
                                    }
                                }
                            } as any,
                        }}

                        minDate={dayjs().subtract(20, 'year')}
                        maxDate={dayjs().add(20, 'year')}
                        value={currentValue ? dayjs(currentValue) : null}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '0',
                                border: 'none',
                                outline: 'none'

                            },
                            '& .MuiInputBase-root': {
                                ...dateFieldFontStyle["md2"],

                            },
                            '& .MuiOutlinedInput-input': {
                                border: 'none',
                                outline: 'none',
                            },
                            '& .MuiInputBase-input': {
                                textAlign: 'center',
                            },
                        }}
                        shouldRespectLeadingZeros={true}
                        onChange={(value, context) => {
                            // console.log('value', value)
                            setCurrentValue(value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </LocalizationProvider>
            ) : (
                value ? value : <div className={styles.placeholder}>DD / MM / YYYY</div>
            )}
        </CellWrapper>
    );
}